import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { Permission } from '../types';
import {
  buildPermissionTree,
  checkedPermissionIds,
  permissionCheckState,
  togglePermissionNode,
  type PermissionCheckState,
  type PermissionNode,
} from '../permissions/permissionAccess';

interface PermissionTreeEditorProps {
  permissions: Permission[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  roleKey: string;
}

const PermissionTreeEditor: React.FC<PermissionTreeEditorProps> = ({ permissions, selectedIds, onChange, roleKey }) => {
  const tree = useMemo(() => buildPermissionTree(permissions), [permissions]);
  const checked = useMemo(() => checkedPermissionIds(permissions, selectedIds), [permissions, selectedIds]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const checkedNow = checkedPermissionIds(permissions, selectedIds);
    const next: Record<number, boolean> = {};
    const walk = (node: PermissionNode) => {
      if (node.children.length === 0) return;
      const state = permissionCheckState(node, checkedNow);
      next[node.permission.id] = state !== 'off';
      node.children.forEach(walk);
    };
    tree.forEach(walk);
    setExpanded(next);
    // Open groups that already have a selection when the role being edited changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKey, permissions]);

  if (permissions.length === 0) {
    return <p className="text-sm text-neutral-500 text-center py-4">No permissions available</p>;
  }

  return (
    <div className="max-h-96 overflow-y-auto border border-neutral-200 rounded-md p-2 bg-neutral-50 space-y-1">
      {tree.map((node) => (
        <PermissionTreeNode
          key={node.permission.id}
          node={node}
          depth={0}
          checked={checked}
          expanded={expanded}
          onToggleExpanded={(id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))}
          onToggleChecked={(item, turnOn) => onChange(togglePermissionNode(permissions, selectedIds, item, turnOn))}
        />
      ))}
    </div>
  );
};

function PermissionTreeNode({
  node,
  depth,
  checked,
  expanded,
  onToggleExpanded,
  onToggleChecked,
}: {
  node: PermissionNode;
  depth: number;
  checked: Set<number>;
  expanded: Record<number, boolean>;
  onToggleExpanded: (id: number) => void;
  onToggleChecked: (node: PermissionNode, turnOn: boolean) => void;
}) {
  const state = permissionCheckState(node, checked);
  const hasChildren = node.children.length > 0;
  const isOpen = expanded[node.permission.id] ?? false;
  const selectedChildren = hasChildren
    ? node.children.filter((child) => permissionCheckState(child, checked) !== 'off').length
    : 0;

  return (
    <div>
      <div
        className="flex items-start gap-2 rounded-md border border-transparent bg-white px-2 py-2 hover:bg-blue-50"
        style={{ marginLeft: depth * 16 }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="mt-0.5 p-0.5 text-neutral-500 hover:text-neutral-800"
            aria-label={isOpen ? `Collapse ${node.permission.name}` : `Expand ${node.permission.name}`}
            onClick={() => onToggleExpanded(node.permission.id)}
          >
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="w-5 shrink-0" />
        )}
        <TriStateCheckbox
          id={`permission-${node.permission.id}`}
          state={state}
          onChange={() => onToggleChecked(node, state !== 'on')}
        />
        <div className="min-w-0 flex-1">
          <label htmlFor={`permission-${node.permission.id}`} className="block cursor-pointer text-sm font-medium text-neutral-900">
            {node.permission.name}
            {hasChildren && state === 'partial' && (
              <span className="ml-2 text-xs font-normal text-neutral-500">
                {selectedChildren} of {node.children.length}
              </span>
            )}
          </label>
          {node.permission.description && (
            <p className="mt-0.5 text-xs text-neutral-600">{node.permission.description}</p>
          )}
        </div>
      </div>
      {hasChildren && isOpen && (
        <div className="mt-1 space-y-1 border-l border-neutral-200 ml-4">
          {node.children.map((child) => (
            <PermissionTreeNode
              key={child.permission.id}
              node={child}
              depth={depth + 1}
              checked={checked}
              expanded={expanded}
              onToggleExpanded={onToggleExpanded}
              onToggleChecked={onToggleChecked}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TriStateCheckbox({
  id,
  state,
  onChange,
}: {
  id: string;
  state: PermissionCheckState;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'partial';
  }, [state]);

  return (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={state === 'on'}
      onChange={onChange}
      className="mt-1 h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
    />
  );
}

export default PermissionTreeEditor;
